/**
 * Storage capability reporting.
 *
 * Section 05 requires that quota exhaustion and private-browsing storage
 * limitations are reported as *reduced offline capability* rather than being
 * swallowed. The two failure shapes are different and are treated differently:
 *
 *   - Storage that is not there at all. A browser with site data blocked, or a
 *     private window that refuses IndexedDB, fails when the database is opened.
 *     Nothing can be queued locally, and the interface has to say so.
 *   - Storage that is there but full. Writes start failing with
 *     `QuotaExceededError` partway through a session. Existing history is still
 *     readable; new local persistence is not.
 *
 * Neither is allowed to be presented as a message that was safely queued.
 */

export type StorageCapability = 'full' | 'reduced' | 'unavailable';

export interface StorageReport {
  capability: StorageCapability;
  /** Bytes currently attributed to this origin, when the browser reports one. */
  usedBytes: number | null;
  quotaBytes: number | null;
  /** True once a write has actually failed for space, not merely looked tight. */
  quotaExceeded: boolean;
  /** True when the origin's data survives eviction pressure. */
  persisted: boolean;
}

/**
 * True when the browser refused a write because the origin is out of space.
 *
 * Browsers disagree on the shape: Chromium throws a `DOMException` named
 * `QuotaExceededError`, Firefox has historically used the legacy code 22 with
 * an `NS_ERROR_DOM_QUOTA_REACHED` message, and Dexie wraps the original in its
 * own error whose `name` is `QuotaExceededError` but whose prototype is not
 * `DOMException`. Matching on the name and the legacy code covers all three
 * without depending on any one engine's class hierarchy.
 */
export function isQuotaExceeded(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const candidate = error as { name?: unknown; code?: unknown; inner?: unknown };
  if (candidate.name === 'QuotaExceededError' || candidate.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
    return true;
  }
  if (candidate.code === 22 || candidate.code === 1014) {
    return true;
  }
  if (candidate.inner !== undefined && candidate.inner !== error) {
    return isQuotaExceeded(candidate.inner);
  }
  return false;
}

/**
 * Fraction of the reported quota above which offline capability is announced as
 * reduced before any write has actually failed.
 *
 * Warning early matters because the failure the user cares about — a message
 * that cannot be saved — happens at the worst possible moment. Ninety percent
 * leaves room for the pages already in flight.
 */
const REDUCED_CAPABILITY_THRESHOLD = 0.9;

/** What the store has observed about this origin's storage so far. */
export interface StorageTrouble {
  /** A write was refused for space. */
  quotaExceeded: boolean;
  /** The database could not be opened at all. */
  unavailable: boolean;
}

export async function measureStorage(trouble: StorageTrouble): Promise<StorageReport> {
  const { quotaExceeded } = trouble;
  let usedBytes: number | null = null;
  let quotaBytes: number | null = null;
  let persisted = false;

  const storage = 'storage' in navigator ? navigator.storage : undefined;
  if (storage && typeof storage.estimate === 'function') {
    try {
      const estimate = await storage.estimate();
      usedBytes = estimate.usage ?? null;
      quotaBytes = estimate.quota ?? null;
    } catch {
      // An estimate is a nicety. A browser that refuses to give one is not a
      // reason to claim storage is broken.
    }
  }
  if (storage && typeof storage.persisted === 'function') {
    try {
      persisted = await storage.persisted();
    } catch {
      persisted = false;
    }
  }

  let capability: StorageCapability = 'full';
  if (trouble.unavailable) {
    capability = 'unavailable';
  } else if (quotaExceeded) {
    capability = 'reduced';
  } else if (usedBytes !== null && quotaBytes !== null && quotaBytes > 0) {
    if (usedBytes / quotaBytes >= REDUCED_CAPABILITY_THRESHOLD) {
      capability = 'reduced';
    }
  }

  return { capability, usedBytes, quotaBytes, quotaExceeded, persisted };
}

/** Human-readable byte size for the storage line in Settings. */
export function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit] ?? 'B'}`;
}
